import { memo } from 'react';
import type { AvatarSizes } from '@frameio/vapor';
import { Avatar, Box, theme } from '@frameio/vapor';
import { IconCompareLink } from '@frameio/vapor-icons';

type Props = {
  alt: string;
  size: AvatarSizes;
  src?: string | null;
  isDisabled?: boolean;
  isWorkfront?: boolean;
  shouldFadeOnLoad?: boolean;
};

const workspaceAvatarSurfaceProps = {
  background: 'transparent' as const,
  shape: 'square' as const,
};

/**
 * Grid fallback — aligned with @frameio/workspaces WorkspaceAvatar/DefaultAvatar
 * (rounded square background + 2×2 grid glyph).
 */
function WorkspaceGridFallback({ size }: { size: number }) {
  return (
    <svg
      fill="none"
      height={size}
      viewBox="0 0 40 40"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0h40v40H0z" fill={theme.colors.background.primary} />
      <path
        d="M17.708 18.958h-3.333c-.69 0-1.25-.56-1.25-1.25v-3.333c0-.69.56-1.25 1.25-1.25h3.333c.69 0 1.25.56 1.25 1.25v3.333c0 .69-.56 1.25-1.25 1.25zM25.624 18.958h-3.333c-.69 0-1.25-.56-1.25-1.25v-3.333c0-.69.56-1.25 1.25-1.25h3.333c.69 0 1.25.56 1.25 1.25v3.333c0 .69-.56 1.25-1.25 1.25zM17.708 26.874h-3.333c-.69 0-1.25-.56-1.25-1.25v-3.333c0-.69.56-1.25 1.25-1.25h3.333c.69 0 1.25.56 1.25 1.25v3.333c0 .69-.56 1.25-1.25 1.25zM25.624 26.874h-3.333c-.69 0-1.25-.56-1.25-1.25v-3.333c0-.69.56-1.25 1.25-1.25h3.333c.69 0 1.25.56 1.25 1.25v3.333c0 .69-.56 1.25-1.25 1.25z"
        stroke={theme.colors.icon.secondary}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeMiterlimit="10"
        strokeWidth="1.5"
      />
    </svg>
  );
}

/** Workfront default — matches @frameio/workspaces WorkspaceAvatar/WorkfrontDefaultAvatar */
function WorkspaceWorkfrontFallback({ size }: { size: number }) {
  return (
    <svg
      fill="none"
      height={size}
      viewBox="0 0 40 40"
      width={size}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0h40v40H0z" fill={theme.colors.background.primary} />
      <g transform="translate(8, 8)">
        <IconCompareLink
          color={theme.colors.icon.secondary}
          data-testid="workfront-avatar"
          size={24}
        />
      </g>
    </svg>
  );
}

function avatarPixelSize(size: AvatarSizes): number {
  if (typeof size === 'number') {
    return size;
  }
  const n = Number(size);
  return Number.isFinite(n) ? n : 24;
}

/**
 * Workspace avatar for settings and tables: same surface as primary-nav workspace
 * rows (Vapor `Avatar`, square, background + grid or Workfront glyph when no image).
 */
export const AvatarForWorkspace = memo(
  ({
    alt,
    size,
    src,
    isDisabled = false,
    isWorkfront = false,
    shouldFadeOnLoad,
  }: Props) => {
    const px = avatarPixelSize(size);
    const gridFallback = <WorkspaceGridFallback size={px} />;
    const workfrontFallback = <WorkspaceWorkfrontFallback size={px} />;
    const whenNoImage = isWorkfront ? workfrontFallback : gridFallback;

    const avatar = src ? (
      <Avatar
        {...workspaceAvatarSurfaceProps}
        alt={alt}
        role="img"
        shouldFadeOnLoad={shouldFadeOnLoad}
        size={size}
        src={src}
      >
        {gridFallback}
      </Avatar>
    ) : (
      <Avatar {...workspaceAvatarSurfaceProps} alt={alt} role="img" size={size}>
        {whenNoImage}
      </Avatar>
    );

    return (
      <Box
        display="flex"
        style={{
          lineHeight: theme.lineHeights[50],
          opacity: isDisabled ? 0.4 : 1,
        }}
      >
        {avatar}
      </Box>
    );
  }
);
